/** The Workshop section of the "New booking" form: the "Choose a workshop"
 *  CTA (none picked yet) or the picked-workshop summary card ("Change"). */
import { Calendar } from 'lucide-react';

import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

import type { PickedWorkshop } from './CreateBookingModalHelpers';
import { formatPickerDate, formatPickerTime } from './WorkshopPickerHelpers';
import { WorkshopTypeTile } from './WorkshopTypeTile';

export function WorkshopCta({ onOpenPicker }: { onOpenPicker: () => void }) {
  // "Every region" is a promise the picker no longer keeps for a viewer confined
  // to their own regions (BON-382). Same positive test as the picker's own: an
  // absent permission means "not known yet", not "confined".
  const { hasPermission } = usePermissions();
  const confined =
    !hasPermission(Permission.BREADTH_WRITE_ALL) &&
    hasPermission(Permission.BREADTH_WRITE_ASSIGNED);

  return (
    <button
      type="button"
      onClick={onOpenPicker}
      className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-moss-300 bg-moss-50/40 p-4 text-left transition hover:border-moss-500 dark:border-moss-700 dark:bg-moss-900/10"
    >
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-moss-100 text-moss-700 dark:bg-moss-900/40 dark:text-moss-300">
        <Calendar size={18} />
      </span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-bark-900 dark:text-stone-100">
          Choose a workshop
        </span>
        <span className="block text-xs text-stone-500">
          {confined ? 'Browse your regions by month' : 'Browse every region & month'}
        </span>
      </span>
      <span className="flex-shrink-0 rounded-full bg-moss-600 px-3 py-1.5 text-xs font-semibold text-white">
        Browse
      </span>
    </button>
  );
}

export function WorkshopCard({
  workshop,
  onOpenPicker,
}: {
  workshop: PickedWorkshop;
  onOpenPicker: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-stone-50 p-3 dark:bg-[#1e2520]">
      <WorkshopTypeTile name={workshop.treeSpeciesName} size={44} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-bark-900 dark:text-stone-100">
          {workshop.typeName} &middot; {workshop.venueName}
        </div>
        <div className="truncate text-xs text-stone-500 dark:text-stone-400">
          {formatPickerDate(workshop.startsAt)} &middot; {formatPickerTime(workshop.startsAt)}
          {' · '}
          {workshop.seatsLeft} seats left
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenPicker}
        className="flex-shrink-0 rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white dark:border-stone-600 dark:text-stone-300"
      >
        Change
      </button>
    </div>
  );
}
